import React, { RefObject } from "react";
import { Input, DatePicker, Select, TableColumnType, InputRef } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps, FilterDropdownProps } from "antd/lib/table/interface";
import { translate } from "shared/languages";
import { tzDate } from "shared/utils/dayjs";
import { formatDateNoTimeToLocalString } from "shared/utils";
import { colors } from "shared/theme/color";
import { toE164 } from "shared/utils/phone";

type SearchType = "text" | "number" | "date" | "enum" | "phone";

type EnumOption<V> = { label: React.ReactNode; value: V };

type SearchConfig<T, K extends keyof T> = {
  dataIndex: K;
  searchType?: SearchType;
  enumOptions?: EnumOption<T[K]>[];
  placeholder?: string;
};

type CustomSearchProps<T extends object, K extends keyof T> = {
  config: SearchConfig<T, K> | K;
  searchInput?: RefObject<InputRef>;
};

/**
 * Search-based <Input /> ColumnProps
 * @returns
 */
export const getColumnSearchProps = <T extends object, K extends keyof T>({
  config,
  searchInput,
}: CustomSearchProps<T, K>): TableColumnType<T> => {
  // Handle both object config and simple dataIndex
  const searchConfig = typeof config === "object" ? config : { dataIndex: config };
  const { dataIndex, searchType, enumOptions } = searchConfig;

  const renderSearchInput = (
    setSelectedKeys: (keys: React.Key[]) => void,
    selectedKeys: React.Key[],
    confirm: (params?: FilterConfirmProps) => void
  ) => {
    const commonStyle = { marginBottom: 8, display: "block" };

    // Otherwise, render based on searchType
    switch (searchType) {
      case "date":
        return (
          <DatePicker
            placeholder={translate("button.search")}
            value={selectedKeys[0] ? tzDate(selectedKeys[0] as string) : null}
            onChange={(date) => {
              setSelectedKeys(date ? [date.format("DD/MM/YYYY")] : []);
              confirm({ closeDropdown: false });
            }}
            style={{ ...commonStyle, width: "100%" }}
          />
        );

      case "enum":
        return (
          <Select
            placeholder={translate("button.search")}
            value={selectedKeys[0]}
            allowClear
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              confirm({ closeDropdown: false });
            }}
            style={{ ...commonStyle, width: "100%" }}
            options={enumOptions}
          />
        );

      default: // text & numbers
        return (
          <Input
            ref={searchInput}
            placeholder={translate("button.search")}
            value={selectedKeys[0] as string}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
              confirm({ closeDropdown: false });
            }}
            style={commonStyle}
          />
        );
    }
  };

  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        {renderSearchInput(setSelectedKeys, selectedKeys, confirm)}
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? colors.navy : undefined }} />,
    onFilter: (value, record) => {
      const fieldValue = record[dataIndex];
      if (fieldValue === null || fieldValue === undefined) {
        return false;
      }

      // Otherwise, use searchType-based filtering
      switch (searchType) {
        case "date":
          return formatDateNoTimeToLocalString(fieldValue as any).includes(value as string);
        case "enum":
          return fieldValue === value;
        case "phone": {
          // Match on digits alone, so "812", "0812", "62812" and
          // "+62 812-3456-7890" all find the stored "+6281234567890".
          const typed = String(value).replace(/\D/g, "");
          if (!typed) return true;
          const stored = fieldValue.toString().replace(/\D/g, "");
          const canonical = toE164(String(value))?.replace(/\D/g, "");
          return stored.includes(typed) || (!!canonical && stored.includes(canonical));
        }
        default: // text
          return fieldValue
            .toString()
            .toLowerCase()
            .includes((value as string).toLowerCase());
      }
    },
    // No `render` here on purpose. AntD already renders the cell text when a
    // column has none, and a render in this object silently replaced the
    // column's own render wherever the spread came after it (bold names, the
    // avatar column, promo chips: about twenty columns rendered plain text
    // for a year because of it).
  };
};

// Convenience functions for common use cases
export const getTextSearchProps = <T extends object, K extends keyof T>(dataIndex: K, searchInput?: RefObject<InputRef>) =>
  getColumnSearchProps<T, K>({
    config: { dataIndex, searchType: "text" } as SearchConfig<T, K>,
    searchInput,
  });

export const getNumberSearchProps = <T extends object, K extends keyof T>(dataIndex: K, searchInput?: RefObject<InputRef>) =>
  getColumnSearchProps<T, K>({
    config: { dataIndex, searchType: "number" } as SearchConfig<T, K>,
    searchInput,
  });

export const getDateSearchProps = <T extends object, K extends keyof T>(dataIndex: K, searchInput?: RefObject<InputRef>) =>
  getColumnSearchProps<T, K>({
    config: { dataIndex, searchType: "date" } as SearchConfig<T, K>,
    searchInput,
  });

export const getEnumSearchProps = <T extends object, K extends keyof T>(
  dataIndex: K,
  enumOptions: EnumOption<T[K]>[],
  searchInput?: RefObject<InputRef>
) =>
  getColumnSearchProps<T, K>({
    config: { dataIndex, searchType: "enum", enumOptions } as SearchConfig<T, K>,
    searchInput,
  });
